import { useMemo, useState } from "react";
import {
  grok45Charts,
  grok45Efficiency,
  kimiK3ChartGroups,
  kimiK3Charts,
  kimiK3Release,
  type ReleaseChart,
} from "../data/releaseCharts";
import { modelById, sourceById } from "../data/v1";
import type { BenchmarkId } from "../types";
import { Icon } from "./Icons";
import { LabLogo } from "./LabLogo";

type ReleaseId = "kimi-k3" | "grok-4-5";

interface ReleaseChartsViewProps {
  onCompare: (benchmarkId: BenchmarkId) => void;
}

const releaseOptions = [
  {
    id: "kimi-k3" as const,
    name: "Kimi K3",
  },
  {
    id: "grok-4-5" as const,
    name: "Grok 4.5",
  },
];

const sourceValue = (value: number, unit: ReleaseChart["unit"]) => {
  if (unit === "Elo" || unit === "tokens") return Math.ceil(value / 100) * 100;
  return 100;
};

function ModelMark({ modelId }: { modelId: string }) {
  const ownerId = modelById.get(modelId)?.ownerId ?? "openai";
  return <LabLogo className="model-mark" labId={ownerId} tone="dark" />;
}

function KimiBarChart({
  chart,
  pointLimit,
  onCompare,
}: {
  chart: ReleaseChart;
  pointLimit?: number;
  onCompare: (benchmarkId: BenchmarkId) => void;
}) {
  const points = useMemo(
    () => [...chart.points].sort((first, second) => second.value - first.value).slice(0, pointLimit),
    [chart, pointLimit],
  );
  const axisMaximum = Math.max(sourceValue(points[0]?.value ?? 100, chart.unit), 1);

  return (
    <article className="kimi-chart">
      <div className="kimi-chart__head">
        <h3>{chart.title}</h3>
        {chart.comment ? <p>{chart.comment}</p> : null}
      </div>
      <div className="kimi-chart__rows">
        {points.map((point) => {
          const model = modelById.get(point.modelId);
          return (
            <div className="kimi-chart-row" key={`${chart.id}-${point.modelId}`}>
              <span className="kimi-chart-row__model">
                <span>{model?.name ?? point.sourceLabel}</span>
                <ModelMark modelId={point.modelId} />
              </span>
              <span className="kimi-chart-row__track">
                <span
                  className={point.modelId === "kimi-k3" ? "kimi-chart-row__fill is-kimi" : "kimi-chart-row__fill"}
                  style={{ "--bar-size": `${(point.value / axisMaximum) * 100}%` } as React.CSSProperties}
                />
              </span>
              <strong>{point.displayValue}</strong>
            </div>
          );
        })}
      </div>
      <button className="dark-text-link" onClick={() => onCompare(chart.benchmarkId)}>
        Data <Icon name="arrow" size={14} />
      </button>
    </article>
  );
}

function KimiChartBook({ onCompare }: ReleaseChartsViewProps) {
  const [groupId, setGroupId] = useState(kimiK3ChartGroups[0].id);
  const selectedGroup =
    kimiK3ChartGroups.find((group) => group.id === groupId) ?? kimiK3ChartGroups[0];
  const groupCharts = selectedGroup.benchmarkIds
    .map((benchmarkId) => kimiK3Charts.find((chart) => chart.benchmarkId === benchmarkId))
    .filter((chart): chart is ReleaseChart => Boolean(chart));
  const source = sourceById.get(kimiK3Release.id);

  return (
    <section className="release-canvas release-canvas--kimi" aria-labelledby="kimi-chart-book-title">
      <div className="release-canvas__source-head">
        <h2 id="kimi-chart-book-title">Kimi K3</h2>
        <a href={source?.url} rel="noreferrer" target="_blank">
          Source <Icon name="external" size={15} />
        </a>
      </div>

      <div className="source-chart-tabs" role="tablist" aria-label="Kimi K3 chart groups">
        {kimiK3ChartGroups.map((group) => (
          <button
            aria-selected={selectedGroup.id === group.id}
            key={group.id}
            onClick={() => setGroupId(group.id)}
            role="tab"
          >
            {group.title}
            <span>{group.benchmarkIds.length}</span>
          </button>
        ))}
      </div>

      <div className="kimi-sheet">
        <header className="kimi-sheet__title">
          <h2>{selectedGroup.title}</h2>
        </header>
        <div className="kimi-sheet__grid">
          {groupCharts.map((chart) => (
            <KimiBarChart
              chart={chart}
              key={chart.id}
              onCompare={onCompare}
              pointLimit={selectedGroup.pointLimit}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function GrokVerticalChart({
  chart,
  onCompare,
}: {
  chart: ReleaseChart;
  onCompare: (benchmarkId: BenchmarkId) => void;
}) {
  const highest = Math.max(...chart.points.map((point) => point.value));
  const axisMaximum = Math.ceil((highest + 4) / 10) * 10;
  const tickMaximum = Math.floor(axisMaximum / 20) * 20;
  const ticks = Array.from({ length: tickMaximum / 20 + 1 }, (_, index) => index * 20);

  return (
    <div className="grok-chart">
      <div className="grok-chart__plot">
        <div className="grok-chart__axis" aria-hidden="true">
          {ticks.map((tick) => (
            <span key={tick} style={{ bottom: `${(tick / axisMaximum) * 100}%` }}>
              <i>{tick}%</i>
            </span>
          ))}
        </div>
        <div className="grok-chart__bars">
          {chart.points.map((point) => (
            <div className="grok-column" key={`${chart.id}-${point.modelId}`}>
              <div className="grok-column__bar-space">
                <span className="grok-column__value" style={{ bottom: `${(point.value / axisMaximum) * 100}%` }}>
                  {point.displayValue}
                </span>
                <span
                  className={point.modelId === "grok-4-5" ? "grok-column__bar is-grok" : "grok-column__bar"}
                  style={{ "--column-size": `${(point.value / axisMaximum) * 100}%` } as React.CSSProperties}
                />
              </div>
              <span className="grok-column__label">
                <ModelMark modelId={point.modelId} />
                <span>{point.sourceLabel}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
      <p className="grok-chart__metric">{chart.metric}</p>
      <button className="dark-text-link" onClick={() => onCompare(chart.benchmarkId)}>
        Data <Icon name="arrow" size={14} />
      </button>
    </div>
  );
}

function GrokEfficiencyChart({ onCompare }: ReleaseChartsViewProps) {
  const maximum = 70_000;
  return (
    <div className="grok-efficiency">
      <header>
        <h3>Token efficiency</h3>
        <p>Output tokens / task</p>
      </header>
      <div className="grok-efficiency__comparison">
        {grok45Efficiency.points.map((point) => (
          <div
            className={point.modelId === "grok-4-5" ? "grok-efficiency-row is-grok" : "grok-efficiency-row"}
            key={point.modelId}
          >
            <span className="grok-efficiency-row__model">
              <ModelMark modelId={point.modelId} />
              <span>{point.sourceLabel}</span>
            </span>
            <span className="grok-efficiency-row__track">
              <span
                className="grok-efficiency-row__line"
                style={{ "--line-size": `${(point.value / maximum) * 100}%` } as React.CSSProperties}
              />
            </span>
            <strong>{point.displayValue}</strong>
          </div>
        ))}
        <div className="grok-efficiency__scale"><span>0</span><span>70k tokens</span></div>
        <span className="grok-efficiency__callout">4.2× fewer tokens</span>
      </div>
      <button className="dark-text-link" onClick={() => onCompare(grok45Efficiency.benchmarkId)}>
        Data <Icon name="arrow" size={14} />
      </button>
    </div>
  );
}

function GrokChartBook({ onCompare }: ReleaseChartsViewProps) {
  const [chartId, setChartId] = useState(grok45Charts[0].id);
  const efficiencySelected = chartId === grok45Efficiency.id;
  const selectedChart =
    grok45Charts.find((chart) => chart.id === chartId) ?? grok45Charts[0];
  const source = sourceById.get("xai-grok-45");

  return (
    <section className="release-canvas release-canvas--grok" aria-labelledby="grok-chart-book-title">
      <div className="release-canvas__source-head">
        <h2 id="grok-chart-book-title">Grok 4.5</h2>
        <a href={source?.url} rel="noreferrer" target="_blank">
          Source <Icon name="external" size={15} />
        </a>
      </div>

      <div className="grok-panel">
        <div className="grok-tabs" role="tablist" aria-label="Grok 4.5 charts">
          {[...grok45Charts, grok45Efficiency].map((chart) => (
            <button
              aria-selected={chartId === chart.id}
              key={chart.id}
              onClick={() => setChartId(chart.id)}
              role="tab"
            >
              {chart.id === grok45Efficiency.id ? "Token efficiency" : chart.title}
            </button>
          ))}
        </div>
        {efficiencySelected ? (
          <GrokEfficiencyChart onCompare={onCompare} />
        ) : (
          <GrokVerticalChart chart={selectedChart} onCompare={onCompare} />
        )}
      </div>
    </section>
  );
}

export function ReleaseChartsView({ onCompare }: ReleaseChartsViewProps) {
  const [releaseId, setReleaseId] = useState<ReleaseId>("kimi-k3");

  return (
    <section className="release-studio">
      <div className="release-selector" role="tablist" aria-label="Latest model releases">
        {releaseOptions.map((release) => (
          <button
            aria-selected={releaseId === release.id}
            key={release.id}
            onClick={() => setReleaseId(release.id)}
            role="tab"
          >
            <strong>{release.name}</strong>
          </button>
        ))}
      </div>

      {releaseId === "kimi-k3" ? (
        <KimiChartBook onCompare={onCompare} />
      ) : (
        <GrokChartBook onCompare={onCompare} />
      )}

    </section>
  );
}
